import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { RecordForm } from "@/components/RecordForm";
import { PaywallGate } from "@/components/PaywallGate";
import { PatientDetail } from "@/pages/PatientDetail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtDate, initials } from "@/lib/format";

const FIELDS = [
  { name: "full_name", label: "Full name", required: true },
  { name: "gender", label: "Gender", type: "select", options: ["male", "female", "other"], default: "male" },
  { name: "dob", label: "Date of birth", type: "date" },
  { name: "phone", label: "Phone" },
  { name: "email", label: "Email" },
  { name: "address", label: "Address", type: "textarea" },
  { name: "blood_group", label: "Blood group", type: "select", options: ["O+", "O-", "A+", "A-", "B+", "B-", "AB+", "AB-"], default: "O+" },
];

export default function Patients() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");

  const { data: patients = [], isLoading } = useQuery({
    queryKey: ["patients"],
    queryFn: async () => (await api.get("/patients")).data || [],
  });

  const mutation = useMutation({
    mutationFn: async (payload) => {
      if (editing?.id) return (await api.put(`/patients/${editing.id}`, payload)).data;
      return (await api.post("/patients", payload)).data;
    },
    onSuccess: () => {
      toast.success("Patient saved");
      qc.invalidateQueries({ queryKey: ["patients"] });
      setOpen(false); setEditing(null);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const filtered = patients.filter((p) =>
    !q || `${p.full_name} ${p.phone} ${p.email} ${p.patient_number}`.toLowerCase().includes(q.toLowerCase())
  );

  const columns = [
    { key: "full_name", label: "Patient", render: (r) => (
      <div className="flex items-center gap-3">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-teal-100 text-xs font-bold text-teal-700">{initials(r.full_name)}</span>
        <div><p className="font-medium text-slate-800">{r.full_name}</p><p className="font-mono text-xs text-slate-400">{r.patient_number || r.id}</p></div>
      </div>
    )},
    { key: "gender", label: "Gender", render: (r) => <span className="capitalize">{r.gender || "—"}</span> },
    { key: "dob", label: "DOB", render: (r) => fmtDate(r.dob) },
    { key: "contact", label: "Contact", render: (r) => (
      <div className="text-xs text-slate-500">
        {r.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {r.phone}</span>}
        {r.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {r.email}</span>}
        {!r.phone && !r.email && "—"}
      </div>
    )},
    { key: "status", label: "Status", type: "badge" },
  ];

  if (selected) return <PatientDetail patient={selected} onBack={() => setSelected(null)} />;

  return (
    <PaywallGate>
      <PageHeader title="Patients" subtitle={`${patients.length} registered`} testId="page-patients">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="patient-search" placeholder="Search patients…" value={q} onChange={(e) => setQ(e.target.value)} className="w-48 pl-9 sm:w-64" />
        </div>
        <Button data-testid="add-patient" className="gap-1.5 bg-teal-700 hover:bg-teal-800" onClick={() => { setEditing(null); setOpen(true); }}>
          <Plus className="h-4 w-4" /> New patient
        </Button>
      </PageHeader>

      <DataTable
        columns={columns} rows={filtered} loading={isLoading}
        onRowClick={(r) => setSelected(r)}
        emptyTitle="No patients yet" emptyDescription="Register your first patient to begin."
        emptyAction="New patient" onEmptyAction={() => setOpen(true)} testId="table-patients"
      />

      <RecordForm open={open} onOpenChange={setOpen} title={editing?.id ? "Edit patient" : "New patient"}
        fields={FIELDS} initial={editing} submitting={mutation.isPending} onSubmit={(p) => mutation.mutate(p)} />
    </PaywallGate>
  );
}
